import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import {
  BrowserRouter,
  Route,
  Routes,
  NavLink,
  Navigate,
} from 'react-router-dom';
import { QueryClientProvider, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutDashboard, Inbox, History, LogOut } from 'lucide-react';
import { AppShell } from '@avagency/frontend/components/shell/AppShell';
import { Button } from '@avagency/frontend/components/ui/button';
import { Skeleton } from '@avagency/frontend/components/ui/skeleton';
import { Toaster } from '@avagency/frontend/components/ui/sonner';
import { createQueryClient } from '@avagency/frontend/lib/api';

import { Dashboard } from './pages/Dashboard';
import { ReviewQueue } from './pages/ReviewQueue';
import { ArchiveHistory } from './pages/ArchiveHistory';
import { SignIn } from './pages/SignIn';
import { ConnectHubSpot } from './pages/ConnectHubSpot';
import {
  AUTH_INVALIDATE_EVENT,
  clearOAuthCallbackStatusFromURL,
  fetchIntegrations,
  fetchMe,
  parseOAuthCallbackStatus,
  signOut,
  type IntegrationListItem,
  type IntegrationListResponse,
  type Me,
  type OAuthCallbackStatus,
} from './api';
import './index.css';

// Kit createQueryClient defaults: staleTime 30s, no refetchOnWindowFocus,
// retry up to 2 for non-4xx, no retry for 4xx. The dashboard is the
// primary query consumer and benefits from these defaults; per-query
// overrides land in useMe/useIntegrations below to tighten freshness
// for auth-critical state.
const queryClient = createQueryClient();

// useMe is the auth gate. /api/v1/auth/me returns the current session
// as JSON (or 401 if unauthenticated). react-query caches the result;
// every render that needs the session subscribes via this hook. A 401
// surfaces as `me.error.status === 401` and the App component routes
// the user to <SignIn />.
//
// Auth state is HELD TO A STRICTER FRESHNESS BAR than dashboard data:
//   - staleTime: 0 — never serve stale auth state from cache. Every
//     refetch trigger (mount, focus, reconnect) goes back to /me.
//   - refetchOnWindowFocus: true — when the user switches back to the
//     tab, re-verify they still have a session. Catches the case of
//     "cookie cleared in another tab" or "session revoked upstream
//     while the tab was idle".
//   - 401 short-circuits the retry loop — that's the "no session"
//     signal, not a transient failure.
//   - Combined with the AUTH_INVALIDATE_EVENT handler in App, any
//     product API call that 401s also invalidates this query — so an
//     in-tab session expiry kicks the gate back to SignIn within one
//     render cycle.
function useMe() {
  return useQuery<Me, Error & { status?: number }>({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    retry: (failureCount, err) => {
      if (err.status === 401) return false;
      return failureCount < 1;
    },
  });
}

// HUBSPOT_PROVIDER is the literal the backend's IntegrationLister
// adapter pins on. Used as a defense-in-depth check in the pickers
// (even if the backend ever leaked another provider's row, the
// dashboard would still never reach for a non-HubSpot integration_id).
const HUBSPOT_PROVIDER = 'hubspot';

function useIntegrations(tenantID: string) {
  return useQuery<IntegrationListResponse, Error & { status?: number }>({
    queryKey: ['integrations', tenantID],
    queryFn: fetchIntegrations,
  });
}

// pickPrimaryActive picks the integration the dashboard should surface
// as "the connection" — the first active HubSpot row, or undefined when
// none exist. Multi-portal-per-tenant lands in a future phase; the v1
// user model is one user = one tenant = one active HubSpot integration.
function pickPrimaryActive(list: IntegrationListItem[] | undefined): IntegrationListItem | undefined {
  if (!list) return undefined;
  return list.find((i) => i.provider === HUBSPOT_PROVIDER && i.status === 'active');
}

// pickReauthRequired surfaces a reauth_required HubSpot row so the
// dashboard can render a "Reconnect" CTA instead of silently failing
// every quota_poll.
function pickReauthRequired(list: IntegrationListItem[] | undefined): IntegrationListItem | undefined {
  if (!list) return undefined;
  return list.find((i) => i.provider === HUBSPOT_PROVIDER && i.status === 'reauth_required');
}

// AuthGate routes between SignIn (no session) and the authenticated
// app shell. While the /me query is in flight, we render a neutral
// loading skeleton so the user never sees the bare SignIn page on a
// hard refresh of an authenticated session.
//
// useEffect listens for AUTH_INVALIDATE_EVENT — fired from
// guardianFetch on any 401 from a non-auth endpoint — and invalidates
// the auth query. This catches every authenticated request path
// (queries, mutations, manual async handlers) with one mechanism, so
// an in-tab session loss kicks the gate back to SignIn within one
// render cycle regardless of which call surfaced the 401.
function App() {
  const me = useMe();
  const qc = useQueryClient();

  useEffect(() => {
    const handler = () => {
      qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    };
    window.addEventListener(AUTH_INVALIDATE_EVENT, handler);
    return () => window.removeEventListener(AUTH_INVALIDATE_EVENT, handler);
  }, [qc]);

  if (me.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8" aria-busy="true">
        <Skeleton className="h-32 w-full max-w-md" />
      </div>
    );
  }
  if (me.isError) {
    if (me.error.status === 401) {
      return <SignIn returnTo={window.location.pathname + window.location.search} />;
    }
    return <SignIn error={`Auth check failed: ${me.error.message}`} />;
  }
  return <AuthenticatedApp session={me.data!} />;
}

// AuthenticatedApp is the shell rendered once /me has returned a
// valid session. The "is the tenant connected to HubSpot?" question
// is answered by /api/v1/integrations via useIntegrations.
//
// Branch precedence (top wins):
//   1. integrations loading → neutral loading skeleton
//   2. integrations error → ConnectHubSpot with error banner (a 401
//      will already have fired AUTH_INVALIDATE_EVENT and the parent
//      App will reroute to SignIn on the next render tick)
//   3. has active integration → connected dashboard shell
//   4. no active, has reauth_required → ConnectHubSpot with
//      reconnect banner
//   5. no integrations at all → ConnectHubSpot onboarding
//
// On post-OAuth-callback navigation (browser arrives at
// "/?av_oauth_status=success" or "...=error&av_oauth_error=..."),
// surface the outcome via the ConnectHubSpot banner. The parse is a
// pure read of window.location captured via useState's lazy initializer
// (StrictMode-safe). The URL cleanup (history.replaceState) lives in
// a useEffect so it runs ONCE post-commit instead of stripping the
// params during render.
function AuthenticatedApp({ session }: { session: Me }) {
  const integrations = useIntegrations(session.tenant_id);

  const [callbackStatus] = React.useState<OAuthCallbackStatus>(parseOAuthCallbackStatus);
  React.useEffect(() => {
    clearOAuthCallbackStatusFromURL();
  }, []);

  if (integrations.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8" aria-busy="true">
        <Skeleton className="h-32 w-full max-w-md" />
      </div>
    );
  }
  if (integrations.isError) {
    // A 401 from /integrations already fired AUTH_INVALIDATE_EVENT
    // via guardianFetch, so the parent App will re-route to SignIn on
    // the next render. For other errors, fall back to the onboarding
    // screen with an explanatory banner — better than a blank state
    // that pretends nothing is wrong.
    return (
      <ConnectHubSpot
        email={session.email}
        onSignOut={() => signOut('/')}
        callbackStatus={{ kind: 'error', message: integrations.error.message }}
      />
    );
  }

  const active = pickPrimaryActive(integrations.data?.integrations);
  const reauthRequired = pickReauthRequired(integrations.data?.integrations);

  if (!active) {
    const banner: OAuthCallbackStatus = reauthRequired
      ? {
          kind: 'error',
          message:
            reauthRequired.last_error ||
            'Your HubSpot connection needs to be re-authorized.',
        }
      : callbackStatus;
    return (
      <ConnectHubSpot
        email={session.email}
        onSignOut={() => signOut('/')}
        callbackStatus={banner}
      />
    );
  }

  return <ConnectedShell session={session} integrationID={active.id} />;
}

// ConnectedShell — the dashboard-with-sidebar surface for tenants
// who have an active HubSpot connection. AppShell provides the
// responsive layout; we own the topbar (brand + signed-in email +
// sign-out) and sidebar nav.
function ConnectedShell({ session, integrationID }: { session: Me; integrationID: string }) {
  useEffect(() => {
    document.title = 'HubSpot Guardian';
  }, []);

  return (
    <BrowserRouter>
      <AppShell
        productSlug="hubspot-guardian"
        topbar={<TopbarBrand email={session.email} />}
        sidebar={<Sidebar />}
      >
        <Routes>
          <Route path="/" element={<Dashboard integrationID={integrationID} />} />
          <Route path="/review" element={<ReviewQueue integrationID={integrationID} />} />
          <Route path="/history" element={<ArchiveHistory />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AppShell>
    </BrowserRouter>
  );
}

function TopbarBrand({ email }: { email: string }) {
  return (
    <div className="flex w-full items-center gap-3">
      {/* Brand orb mark */}
      <div
        className="hg-brand-orb flex-shrink-0"
        style={{ width: 24, height: 24 }}
        aria-hidden="true"
      />
      <span className="text-sm font-semibold tracking-tight">HubSpot Guardian</span>
      <span className="hidden text-xs text-muted-foreground/70 sm:inline">
        Free-tier contact cap watchdog
      </span>
      <div className="ml-auto flex items-center gap-3">
        <span className="hidden text-xs text-muted-foreground sm:inline">{email}</span>
        <Button variant="outline" size="sm" onClick={() => signOut('/')}>
          <LogOut className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
          Sign out
        </Button>
      </div>
    </div>
  );
}

function Sidebar() {
  const linkClass = (isActive: boolean) =>
    [
      'flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150',
      isActive
        ? 'bg-primary/15 text-primary border border-primary/20 shadow-[0_0_12px_rgba(255,122,89,0.12)]'
        : 'text-foreground/70 hover:bg-white/5 hover:text-foreground border border-transparent',
    ].join(' ');

  return (
    <ul className="space-y-1">
      <li>
        <NavLink to="/" end className={({ isActive }) => linkClass(isActive)}>
          <LayoutDashboard className="h-4 w-4" />
          Dashboard
        </NavLink>
      </li>
      <li>
        <NavLink to="/review" className={({ isActive }) => linkClass(isActive)}>
          <Inbox className="h-4 w-4" />
          Review Queue
        </NavLink>
      </li>
      <li>
        <NavLink to="/history" className={({ isActive }) => linkClass(isActive)}>
          <History className="h-4 w-4" />
          Archive History
        </NavLink>
      </li>
    </ul>
  );
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      <Toaster />
    </QueryClientProvider>
  </React.StrictMode>,
);
